import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getCourseById, getAllCourseModules, getLessonsByModuleId } from '../../api/courseService';
import { getPurchaseData, getCourseProgress } from '../../api/packageService';
import ContentPlayer from '../../components/common/ContentPlayer';
import { ArrowLeft, List, Play, X, FileText, HelpCircle, BookOpen, CheckCircle, Clock, Award, Search, Trophy } from 'lucide-react';

interface CourseModule {
  _id: string;
  title: string;
  description: string;
  order: number;
  courseId: {
    _id: string;
    title: string;
  };
}

interface Lesson {
  _id: string;
  title: string;
  description: string;
  content: string;
  lessonThumbnail: string;
  lessonType: string;
  questions: Array<{
    questionText: string;
    options: Array<{
      answertext: string;
      isCorrect: boolean;
      explanation: string;
    }>;
  }>;
  duration?: number;
}

const VideoPlayerPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState<any>(null);
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [currentContentUrl, setCurrentContentUrl] = useState<string>('');
  const [currentLessonTitle, setCurrentLessonTitle] = useState<string>('');
  const [currentLessonDescription, setCurrentLessonDescription] = useState<string>('');
  const [currentLessonType, setCurrentLessonType] = useState<string>('video');
  const [currentQuestions, setCurrentQuestions] = useState<Lesson['questions']>([]);
  const [loading, setLoading] = useState(true);
  const [loadingModules, setLoadingModules] = useState(true);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [expandedModules, setExpandedModules] = useState<{[key: string]: boolean}>({});
  const [currentLessonId, setCurrentLessonId] = useState<string | null>(null);
  // const [completedLessons, setCompletedLessons] = useState<string[]>([]);
  // const [courseProgress, setCourseProgress] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [totalLessons, setTotalLessons] = useState<number>(0);
  // const [completedModules, setCompletedModules] = useState<{[key: string]: number}>({});
  const [, setPurchaseId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [currentVideoDuration, setCurrentVideoDuration] = useState<number>(0);
  // const completedLessonsRef = useRef<string[]>([]);
  
  // Add state for course progress data
  const [courseProgressData, setCourseProgressData] = useState<any>(null);
  const [lessonProgress, setLessonProgress] = useState<Record<string, number>>({});
  const [moduleLessons, setModuleLessons] = useState<Record<string, Lesson[]>>({});

  // Get user data from localStorage
  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        setUserId(userData._id);
        const name = userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email || '';
        setUserName(name);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Fetch purchase data to get purchaseId - FIXED
  const fetchPurchaseData = async () => {
    if (!userId || !courseId) return;
    
    try {
      console.log('Fetching purchase data for user:', userId, 'course:', courseId);
      const response = await getPurchaseData(userId);
      console.log('Purchase data response:', response);
      
      if (response.status === 200 && response.data) {
        // Find the purchase that contains this course
        const purchase = response.data.purchases.find((p: any) => 
          p.courses.some((c: any) => c.courseId === courseId)
        );
        
        console.log('Found purchase:', purchase);
        
        if (purchase && purchase.purchaseId) {
          setPurchaseId(purchase.purchaseId);
          
          // Load saved progress for this course but don't use it
          const courseData = purchase.courses.find((c: any) => c.courseId === courseId);
          if (courseData) {
            // Don't set the actual progress, keep it at 0
            console.log('Loaded saved progress (but not using it):', courseData.courseViewPercent);
          }
        } else {
          console.log('No purchase found for this course');
        }
      } else {
        console.log('Failed to fetch purchase data:', response.message);
      }
    } catch (error) {
      console.error('Error fetching purchase data:', error);
    }
  };

// Fetch course progress data
const fetchCourseProgressData = useCallback(async () => {
  if (!userId || !courseId) return;

  try {
    const response = await getCourseProgress(courseId, userId);

    console.log("📊 FULL COURSE PROGRESS RESPONSE:", response);

    if (response.status === 200 && response.data?.getCourseProgress) {
      const progressData = response.data.getCourseProgress;

      console.log("📊 COURSE PROGRESS:", progressData);
      console.log("📊 TOTAL PROGRESS:", progressData.totalProgressPercent);
      console.log("📊 LESSON PROGRESS:", progressData.lessonProgress);

      // Store the complete backend response.
      // This is the source of truth for all percentage-based UI.
      setCourseProgressData(progressData);

      /*
       * lessonProgress is intentionally a WATCH-TIME map.
       *
       * lessonProgress[lessonId] = seconds watched
       *
       * Do NOT store progressPercent here because this map
       * is also used by getResumeTime().
       */
      const watchTimeMap: Record<string, number> = {};

      if (Array.isArray(progressData.lessonProgress)) {
        progressData.lessonProgress.forEach((lesson: any) => {
          if (!lesson?.lessonId) return;

          watchTimeMap[lesson.lessonId] = Number(lesson.watchTime) || 0;
        });
      }

      console.log("🕐 WATCH TIME MAP:", watchTimeMap);

      setLessonProgress(watchTimeMap);
    }
  } catch (error) {
    console.error("Error fetching course progress:", error);
  }
}, [userId, courseId]);
  
  // Add effect to fetch course progress when userId or courseId changes
  useEffect(() => {
    if (userId && courseId) {
      fetchPurchaseData();
      fetchCourseProgressData();
    }
  }, [userId, courseId, fetchCourseProgressData]);
  
  // Add refresh effect for page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCourseProgressData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchCourseProgressData]);

  // Remove the updateProgressInBackend function or make it a no-op
  // const updateProgressInBackend = async (progress: number) => {
  //   // Do nothing - disable backend progress updates
  //   console.log('Progress update disabled - not sending to backend:', progress);
  //   return;
  // };

  // Fetch course data
  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) return;
      try {
        setLoading(true);
        const response = await getCourseById(courseId);
        if (response.status === 200 && response.data) {
          setCourse(response.data);
          setCurrentContentUrl(response.data.thumbnail);
          setCurrentLessonTitle("Select a lesson to begin");
          setCurrentLessonDescription("Please select a lesson from the course content sidebar to start learning.");
          setCurrentLessonType('video');
        } else {
          setError(response.message || "Failed to load course details");
        }
      } catch (err) {
        console.error("Error fetching course:", err);
        setError("Please check your authentication and try again");
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [courseId]);

  // Fetch modules and lessons
  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoadingModules(true);
        const response = await getAllCourseModules();
        
        if (response.status === 200 && response.data) {
          const filteredModules = response.data.filter(
            (module: CourseModule) => module.courseId && module.courseId._id === courseId
          );
          
          filteredModules.sort((a: CourseModule, b: CourseModule) => a.order - b.order);
          setModules(filteredModules);
          
         let totalLessonCount = 0;

          const lessonsByModule: Record<string, Lesson[]> = {};

          for (const module of filteredModules) {
            try {
              const lessonsResponse = await getLessonsByModuleId(module._id);

              if (
                lessonsResponse.status === 200 &&
                Array.isArray(lessonsResponse.data)
              ) {
                const moduleLessonsList = lessonsResponse.data;

                lessonsByModule[module._id] = moduleLessonsList;

                totalLessonCount += moduleLessonsList.length;
              } else {
                lessonsByModule[module._id] = [];
              }
            } catch (err) {
              console.error(
                `Error fetching lessons for module ${module._id}:`,
                err
              );

              lessonsByModule[module._id] = [];
            }
          }

          setModuleLessons(lessonsByModule);
          setTotalLessons(totalLessonCount);
          
          const moduleId = searchParams.get('moduleId');
          const lessonId = searchParams.get('lessonId');
          
          if (filteredModules.length > 0) {
            const expandedState: {[key: string]: boolean} = {};
            filteredModules.forEach((module: CourseModule) => {
              expandedState[module._id] = true;
            });
            setExpandedModules(expandedState);
            
            if (moduleId) {
              handleModuleClick(moduleId);
              
              if (lessonId) {
                setTimeout(() => {
                  const lesson = lessons.find(l => l._id === lessonId);
                  if (lesson) {
                    handleLessonClick(lesson);
                  }
                }, 500);
              }
            } else {
              handleModuleClick(filteredModules[0]._id);
            }
          }
        } else {
          console.error("Failed to fetch modules:", response.message);
        }
      } catch (err) {
        console.error("Error fetching modules:", err);
      } finally {
        setLoadingModules(false);
      }
    };
    if (courseId) {
      fetchModules();
    }
  }, [courseId]);

  const handleModuleClick = async (moduleId: string) => {
    if (selectedModuleId === moduleId) {
      setExpandedModules(prev => ({
        ...prev,
        [moduleId]: !prev[moduleId]
      }));
      return;
    }
    setSelectedModuleId(moduleId);
    setLoadingLessons(true);
    
    try {
      const response = await getLessonsByModuleId(moduleId);
      console.log("Lessons response:", response);
      
      if (response.status === 200 && response.data) {
        setLessons(response.data);
        
        setExpandedModules(prev => ({
          ...prev,
          [moduleId]: true
        }));
        
        if (response.data.length > 0) {
          const firstLesson = response.data[0];
          console.log("Auto-playing first lesson:", firstLesson);
          handleLessonClick(firstLesson);
        }
      } else {
        setLessons([]);
      }
    } catch (err) {
      console.error("Error fetching lessons:", err);
      setLessons([]);
    } finally {
      setLoadingLessons(false);
    }
  };

  const getModuleCompletion = (moduleId: string) => {
    const moduleLessonsList = moduleLessons[moduleId] || [];

    if (moduleLessonsList.length === 0) {
      return 0;
    }

    const completed = moduleLessonsList.filter(lesson => {
      const progressData = courseProgressData?.lessonProgress?.find(
        (progress: any) => progress.lessonId === lesson._id
      );

      return (progressData?.progressPercent || 0) >= 95;
    }).length;

    return Math.round((completed / moduleLessonsList.length) * 100);
  };
    
  const getModuleCompletedCount = (moduleId: string) => {
  const moduleLessonsList = moduleLessons[moduleId] || [];

  return moduleLessonsList.filter(lesson => {
    const progressData = courseProgressData?.lessonProgress?.find(
      (progress: any) => progress.lessonId === lesson._id
    );

    return (progressData?.progressPercent || 0) >= 95;
  }).length;
};

  const handleLessonClick = (lesson: Lesson) => {
  console.log("Lesson clicked:", lesson);

  const contentUrl =
    lesson.content || lesson.lessonThumbnail || course.thumbnail;

  setCurrentContentUrl(contentUrl);
  setCurrentLessonTitle(lesson.title);
  setCurrentLessonDescription(
    lesson.description || "No description available"
  );
  setCurrentLessonId(lesson._id);
  setCurrentVideoDuration(0);

  const lessonTypeForUI =
    lesson.lessonType === "assignment"
      ? "document"
      : lesson.lessonType || "video";

  setCurrentLessonType(lessonTypeForUI);
  setCurrentQuestions(lesson.questions || []);

  if (window.innerWidth < 768) {
    setShowSidebar(false);
  }
};

  // Mark lesson as completed but don't update progress
  const markLessonAsCompleted = (lessonId: string) => {
    // Don't track lesson completion to avoid showing progress
    console.log('Lesson completion tracking disabled for:', lessonId);
  };

  const getFilteredLessons = (moduleId: string) => {
  const moduleLessonsList = moduleLessons[moduleId] || [];

  return moduleLessonsList.filter((lesson) => {
    const title = lesson.title || '';
    const description = lesson.description || '';

    const matchesSearch =
      title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      description.toLowerCase().includes(searchTerm.toLowerCase());

    const lessonTypeForUI =
      lesson.lessonType === 'assignment'
        ? 'document'
        : lesson.lessonType;

    const matchesType =
      filterType === 'all' || lessonTypeForUI === filterType;

    return matchesSearch && matchesType;
  });
};

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds <= 0) return '–';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const mm = m < 10 ? `0${m}` : `${m}`;
    const ss = s < 10 ? `0${s}` : `${s}`;
    return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="text-red-600" size={32} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Course</h2>
          <p className="text-gray-600 mb-4">{error || "Course not found"}</p>
          <button 
            onClick={() => navigate(-1)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Get icon based on lesson type
  const getLessonIcon = (lessonType: string) => {
    switch (lessonType) {
      case 'video': return <Play size={14} />;
      case 'document': return <FileText size={14} />;
      case 'quiz': return <HelpCircle size={14} />;
      default: return <Play size={14} />;
    }
  };

  // Get color based on lesson type
  const getLessonColor = (lessonType: string) => {
    switch (lessonType) {
      case 'video': return 'text-purple-600 bg-purple-100';
      case 'document': return 'text-blue-600 bg-blue-100';
      case 'quiz': return 'text-green-600 bg-green-100';
      default: return 'text-gray-500 bg-gray-100';
    }
  };

  // Update the calculateProgress function to use actual course progress data
  const calculateProgress = () => {
  if (
    courseProgressData &&
    courseProgressData.totalProgressPercent !== undefined
  ) {
    const progress = Number(courseProgressData.totalProgressPercent) || 0;

    return Math.min(Math.round(progress), 100);
  }

  return 0;
};
  
  // Add function to get lesson progress
 const getLessonProgressValue = (lessonId: string) => {
  if (!lessonId) return 0;

  const progressData = courseProgressData?.lessonProgress?.find(
    (lesson: any) => lesson.lessonId === lessonId
  );

  if (!progressData) {
    return 0;
  }

  const progressPercent = Number(progressData.progressPercent) || 0;

  // Backend considers 95%+ as completed.
  // Display completed lessons as 100%.
  if (progressPercent >= 95) {
    return 100;
  }

  return Math.min(Math.max(progressPercent, 0), 100);
};
  
  const getCompletedLessonCount = () => {
    if (!courseProgressData?.lessonProgress) {
      return 0;
    }

    return courseProgressData.lessonProgress.filter(
      (lesson: any) => (lesson.progressPercent || 0) >= 95
    ).length;
  };

  const completedLessonCount = getCompletedLessonCount();

  // Add function to get actual resume time (0 for completed lessons)
  const getResumeTime = (lessonId: string) => {
  if (!lessonId) return 0;

  const progressData = courseProgressData?.lessonProgress?.find(
    (lesson: any) => lesson.lessonId === lessonId
  );

  if (!progressData) {
    return 0;
  }

  const progressPercent = Number(progressData.progressPercent) || 0;

  // Completed lesson → start from beginning
  if (progressPercent >= 95) {
    return 0;
  }

  // Resume from the actual backend watch time
  return Number(progressData.watchTime) || 0;
};

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-md border-b border-gray-200 p-3 flex items-center">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-purple-600 hover:text-purple-800 mr-3 transition-colors duration-300"
        >
          <ArrowLeft className="mr-1" size={18} />
          <span className="hidden sm:inline font-medium text-sm">Back</span>
        </button>
        <div className="flex-1 min-w-0 max-w-2xl">
          <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">
            {course.title}
          </h1>
          <div className="flex items-center mt-1">
            <div className="flex items-center">
              <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full" 
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
              </div>
              <span className="bg-gradient-to-r from-green-400 to-green-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                {calculateProgress()}% Complete
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => setShowSidebar(!showSidebar)}
            className="p-2 rounded-lg hover:bg-purple-50 transition-all duration-300 border border-purple-200 md:hidden"
            title={showSidebar ? "Hide lessons" : "Show lessons"}
          >
            <List size={18} className="text-purple-600" />
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Content Player Container */}
        <div className={`${showSidebar ? 'hidden lg:flex' : 'flex'} flex-1 p-4 bg-gray-900 justify-center overflow-hidden`}>
          <div className="flex flex-col rounded-lg overflow-hidden shadow-xl bg-gray-800 w-full h-full mx-auto">
            <ContentPlayer
              contentType={currentLessonType as 'video' | 'document' | 'quiz'}
              contentUrl={currentContentUrl}
              title={currentLessonTitle}
              description={currentLessonDescription}
              questions={currentQuestions}
              userId={userId || undefined}
              courseId={courseId || undefined}
              lessonId={currentLessonId || undefined}
              userName={userName || undefined}
 
onContentComplete={() => {
  console.log(
    'Refreshing course progress:',
    currentLessonId
  );

  if (currentLessonId) {
    fetchCourseProgressData();
  }
}}            

              resumeTime={getResumeTime(currentLessonId || '')}
              onDurationChange={(secs) => setCurrentVideoDuration(secs)}
            />
          </div>
        </div>
        
        {/* Mobile Sidebar Overlay */}
        {showSidebar && (
          <div className="lg:hidden fixed inset-0 bg-black bg-opacity-60 z-40 backdrop-blur-sm" onClick={() => setShowSidebar(false)}></div>
        )}
        
        {/* Course Info Sidebar */}
        <div className={`${
          showSidebar ? 'translate-x-0' : 'translate-x-full'
        } fixed lg:static inset-y-0 right-0 w-full sm:w-96 lg:w-80 bg-white z-50 lg:z-auto transform transition-all duration-300 ease-in-out lg:translate-x-0 flex flex-col shadow-xl lg:shadow-lg`}>
          {/* Mobile Sidebar Header */}
          <div className="lg:hidden flex items-center justify-between p-3 border-b bg-gradient-to-r from-purple-50 to-blue-50">
            <h2 className="text-base font-bold text-gray-900">Course Content</h2>
            <button 
              onClick={() => setShowSidebar(false)}
              className="p-1.5 rounded-md hover:bg-purple-100 transition-colors duration-300"
            >
              <X size={18} className="text-gray-600" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2.5">
            <h2 className="text-sm font-bold mb-2.5 hidden lg:block text-gray-900">Course Content</h2>
            
            {/* Course Progress Card */}
            <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100 shadow-sm">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-purple-800 text-xs flex items-center">
                  <Award className="mr-1" size={14} />
                  Course Progress
                </h3>
                <span className="text-xs font-bold text-purple-700 bg-white px-1.5 py-0.5 rounded-full">
                  {calculateProgress()}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 shadow-inner mb-1.5">
                <div 
                  className="bg-blue-500 h-1.5 rounded-full shadow-sm transition-all duration-500" 
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <span>{calculateProgress()}%</span>
                <span>{totalLessons} lessons</span>
              </div>
              <div className="flex items-center mt-1.5 text-xs text-gray-600">
                <Trophy size={10} className="mr-1 text-yellow-500" />
               <span> {completedLessonCount} of {totalLessons} lessons completed</span>
              </div>
            </div>
            
            {/* Current Lesson Info */}
            <div className="mb-3 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 shadow-sm">
              <h3 className="font-semibold text-indigo-800 mb-1.5 text-xs flex items-center">
                <BookOpen className="mr-1" size={12} />
                Current Lesson
              </h3>
              <h4 className="font-bold text-gray-900 mb-1 text-sm leading-tight">{currentLessonTitle}</h4>
              <p className="text-xs text-gray-600 leading-relaxed line-clamp-2 mb-1.5">{currentLessonDescription}</p>
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getLessonColor(currentLessonType)}`}>
                  {getLessonIcon(currentLessonType)}
                  <span className="ml-1 capitalize">{currentLessonType}</span>
                </span>
                {currentLessonId && (
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock size={10} className="mr-1" />
                    <span>Duration: {currentVideoDuration > 0 ? formatDuration(currentVideoDuration) : '–'}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Search and Filter */}
            <div className="mb-3">
              <div className="relative mb-1.5">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                  <Search className="h-3.5 w-3.5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search lessons..."
                  className="block w-full pl-8 pr-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex space-x-1.5">
                <button
                  onClick={() => setFilterType('all')}
                  className={`flex-1 text-xs py-1 rounded-lg transition-colors ${
                    filterType === 'all' 
                      ? 'bg-purple-100 text-purple-700 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterType('video')}
                  className={`flex-1 text-xs py-1 rounded-lg transition-colors flex items-center justify-center ${
                    filterType === 'video' 
                      ? 'bg-purple-100 text-purple-700 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Play size={10} className="mr-1" />
                  Video
                </button>
                <button
                  onClick={() => setFilterType('document')}
                  className={`flex-1 text-xs py-1 rounded-lg transition-colors flex items-center justify-center ${
                    filterType === 'document' 
                      ? 'bg-purple-100 text-purple-700 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FileText size={10} className="mr-1" />
                  Doc
                </button>
                <button
                  onClick={() => setFilterType('quiz')}
                  className={`flex-1 text-xs py-1 rounded-lg transition-colors flex items-center justify-center ${
                    filterType === 'quiz' 
                      ? 'bg-purple-100 text-purple-700 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <HelpCircle size={10} className="mr-1" />
                  Quiz
                </button>
              </div>
            </div>
            
            {/* Course Content */}
            <div>
              <h3 className="font-bold text-gray-900 mb-2.5 text-xs flex items-center">
                🎓 Course Modules
                <span className="ml-1.5 bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full">
                  {modules.length} modules
                </span>
              </h3>
              {loadingModules ? (
                <div className="flex justify-center py-5">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {modules.map((module, index) => (
                    <li key={module._id} className="rounded-xl overflow-hidden shadow-sm">
                      <div 
                        className="flex items-center justify-between py-2.5 px-3 bg-gradient-to-r from-gray-50 to-gray-100 cursor-pointer hover:from-purple-50 hover:to-blue-50 transition-all duration-300"
                        onClick={() => handleModuleClick(module._id)}
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <span className="w-6 h-6 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full text-xs flex items-center justify-center mr-2.5 font-bold">
                            {index + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-900 text-xs truncate block">{module.title}</span>
                              <div className="flex items-center">
                                {/* Don't show completed modules indicator */}
<span className="text-xs text-gray-500">
  {getModuleCompletedCount(module._id)}/{moduleLessons[module._id]?.length || 0}
</span>                               
                              </div>
                            </div>
                            {module.description && (
                              <p className="text-xs text-gray-500 truncate mt-0.5">{module.description}</p>
                            )}
                            <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                              <div 
                                className="bg-blue-500 h-1 rounded-full" 
                                style={{ width: `${getModuleCompletion(module._id)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {expandedModules[module._id] && (
                        <div className="bg-white border-t border-gray-200">
                          <div className="py-1.5 px-3">
                            {loadingLessons && selectedModuleId === module._id ? (
                              <div className="flex justify-center py-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-purple-600"></div>
                              </div>
                            ) : (
                              <ul className="space-y-1.5">
                                {getFilteredLessons(module._id).length > 0 ? (
                                  getFilteredLessons(module._id).map((lesson) => (
                                    <li 
                                      key={lesson._id} 
                                      className={`py-2 px-2.5 flex items-center cursor-pointer rounded-lg transition-all duration-300 ${
                                        currentLessonId === lesson._id 
                                          ? 'bg-gradient-to-r from-purple-100 to-blue-100 border-l-2 border-purple-500' 
                                          : 'hover:bg-gray-50'
                                      }`}
                                      onClick={() => handleLessonClick(lesson)}
                                    >
                                      <div className={`mr-2 p-1 rounded ${getLessonColor(lesson.lessonType)}`}>
                                        {getLessonIcon(lesson.lessonType)}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <span className={`text-xs truncate block ${
                                          currentLessonId === lesson._id 
                                            ? 'font-semibold text-purple-600' 
                                            : 'text-gray-800'
                                        }`}>
                                          {lesson.title}
                                        </span>
                                        <div className="flex items-center">
                                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getLessonColor(lesson.lessonType)}`}>
                                            {getLessonIcon(lesson.lessonType)}
                                            <span className="ml-1 capitalize">{lesson.lessonType}</span>
                                          </span>
                                          {lesson.lessonType === 'video' && (
                                            <span className="ml-1.5 text-xs text-gray-500">
                                              {getLessonProgressValue(lesson._id)}%
                                            </span>
                                          )}
                                        </div>
                                        {/* Lesson progress bar - only for video lessons */}
                                        {lesson.lessonType === 'video' && (
                                          <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                                            <div 
                                              className="bg-blue-500 h-1 rounded-full" 
                                              style={{ width: `${getLessonProgressValue(lesson._id)}%` }}
                                            ></div>
                                          </div>
                                        )}
                                        {lesson.duration ? (
                                          <div className="flex items-center text-xs text-gray-500 mt-0.5">
                                            <Clock size={9} className="mr-1" />
                                            <span>{formatDuration(lesson.duration)}</span>
                                          </div>
                                        ) : null}
                                      </div>
                                      {/* Show completed lessons indicator */}
                                      {getLessonProgressValue(lesson._id) >= 100 && (
                                        <CheckCircle size={14} className="text-green-500 ml-1.5" />
                                      )}
                                      {currentLessonId === lesson._id && (
                                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse ml-1.5"></div>
                                      )}
                                    </li>
                                  ))
                                ) : (
                                  <li className="py-2.5 px-2.5 text-gray-500 text-center text-xs bg-gray-50 rounded-lg">
                                    📝 No lessons found
                                  </li>
                                )}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerPage;